import React from 'react';
import { Box, Typography, GlobalStyles } from '@mui/material';
import { FileText, Folder } from 'lucide-react';

// Shared chrome for public /view/:slug pages (shared documents, shared reports,
// shared folders) - dark topbar, no sidebar, no app shell. Kept generic so any
// viewer flavor can reuse the exact same look and feel.
//
// Also carries print support: the on-screen layout clips content to the
// viewport (100vh + overflow hidden, so the page itself never scrolls - only
// the content area does), which would silently cut off anything below the
// fold when printing/exporting to PDF. The print media query below switches
// to auto height + visible overflow so the browser can paginate the full
// content across as many pages as it needs, and hides the dark topbar (not
// meaningful on a printed page).

export const ViewerPrintStyles: React.FC = () => (
    <GlobalStyles
        styles={{
            '@media print': {
                '@page': { size: 'landscape', margin: '10mm' },
                '.viewer-shell-topbar': { display: 'none' },
                '.viewer-shell-root': { height: 'auto !important', overflow: 'visible !important' },
                '.viewer-shell-content': { overflow: 'visible !important' },
                '.no-print': { display: 'none !important' },
            },
        }}
    />
);

interface ViewerShellProps {
    title: string;
    toolbar?: React.ReactNode;
    icon?: 'document' | 'folder';
    children: React.ReactNode;
}

export const ViewerShell: React.FC<ViewerShellProps> = ({ title, toolbar, icon = 'document', children }) => (
    <Box className="viewer-shell-root" sx={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--color-container-background)', overflow: 'hidden' }}>
        <ViewerPrintStyles />
        <Box className="viewer-shell-topbar" sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 3, height: 52, background: '#1a1a2e', flexShrink: 0,
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {icon === 'folder'
                    ? <Folder size={18} color="var(--color-primary)" />
                    : <FileText size={18} color="var(--color-primary)" />
                }
                <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: { xs: 180, sm: 400 } }}>
                    {title}
                </Typography>
            </Box>
            {toolbar}
        </Box>
        <Box className="viewer-shell-content" sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {children}
        </Box>
    </Box>
);

export const CenteredCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ background: 'var(--color-page-background)', borderRadius: 3, p: 5, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 320 }}>
            {children}
        </Box>
    </Box>
);
